/**
 * JoinTripPage - Landing page for invite links (/join/:token)
 *
 * Internally a small state machine covering 5 screens from the design:
 * not-logged-in / confirm / success / invalid / expired.
 * Design: tripways.pen nodes lrhcu / I7hmEn / FQe3f / nRCDK / VrUNK
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Compass, Lock, CalendarDays, Users, MapPin, CircleCheck, CircleX, Clock4 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SignInModal from '../components/auth/SignInModal';
import AvatarStack from '../components/common/AvatarStack';
import { memberService, MemberApiError } from '../services/memberService';
import { useUserProfiles, getAvatarFallback } from '../hooks/useUserProfiles';
import type { InvitePreview, TripMember } from '../types/member';

type Screen = 'loading' | 'not-logged-in' | 'confirm' | 'joining' | 'success' | 'invalid' | 'expired' | 'error';

export default function JoinTripPage() {
  const { token } = useParams<{ token: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [screen, setScreen] = useState<Screen>('loading');
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [members, setMembers] = useState<TripMember[]>([]);
  const [showSignIn, setShowSignIn] = useState(false);
  const [errorCode, setErrorCode] = useState<number | null>(null);

  const { profiles: ownerProfiles } = useUserProfiles(preview ? [preview.ownerId] : []);
  const ownerProfile = preview ? ownerProfiles.get(preview.ownerId) : undefined;

  const { profiles: memberProfileMap } = useUserProfiles(members.map((m) => m.userId));
  const memberProfiles = members
    .map((m) => memberProfileMap.get(m.userId))
    .filter((p): p is NonNullable<typeof p> => p !== undefined);

  useEffect(() => {
    if (authLoading || !token) return;

    if (!isAuthenticated) {
      setScreen('not-logged-in');
      return;
    }

    if (screen !== 'loading') return;

    memberService
      .previewInviteLink(token)
      .then((result) => {
        setPreview(result);
        setScreen('confirm');
      })
      .catch((err) => {
        if (err instanceof MemberApiError) {
          setErrorCode(err.code);
          setScreen(err.code === 60003 ? 'expired' : 'invalid');
        } else {
          setScreen('error');
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, token]);

  // 登入完成後（screen 仍是 not-logged-in）自動重試預覽
  useEffect(() => {
    if (isAuthenticated && screen === 'not-logged-in') {
      setScreen('loading');
    }
  }, [isAuthenticated, screen]);

  const handleJoin = async () => {
    if (!token) return;
    setScreen('joining');
    try {
      const { tripId } = await memberService.joinTrip(token);
      const memberList = await memberService.getMembers(String(tripId));
      setMembers(memberList);
      setScreen('success');
    } catch (err) {
      if (err instanceof MemberApiError) {
        setErrorCode(err.code);
        setScreen(err.code === 60003 ? 'expired' : 'invalid');
      } else {
        setScreen('error');
      }
    }
  };

  if (!token) {
    return (
      <JoinTripLayout>
        <p className="text-slate-600">邀請連結格式不正確。</p>
      </JoinTripLayout>
    );
  }

  if (screen === 'loading' || screen === 'joining') {
    return (
      <JoinTripLayout>
        <Spinner />
      </JoinTripLayout>
    );
  }

  if (screen === 'not-logged-in') {
    return (
      <JoinTripLayout>
        <IconCircle bg="#f0f9ff">
          <Lock size={28} className="text-primary" />
        </IconCircle>
        <h1 className="text-slate-900 text-xl font-bold">請先登入</h1>
        <p className="text-sm text-slate-500 text-center max-w-[340px]">
          登入後即可加入這趟旅程，與夥伴一起規劃行程。
        </p>
        <button
          onClick={() => setShowSignIn(true)}
          className="w-[340px] max-w-full h-12 rounded-3xl bg-primary text-white text-sm font-semibold"
        >
          前往登入
        </button>
        <SignInModal isOpen={showSignIn} onClose={() => setShowSignIn(false)} />
      </JoinTripLayout>
    );
  }

  if (screen === 'confirm' && preview) {
    return (
      <JoinTripLayout>
        <div className="flex flex-col items-center gap-3 w-full">
          <p className="text-[13px] text-slate-400">你被邀請加入</p>
          <h1 className="text-slate-900 text-[22px] font-bold text-center">{preview.tripName}</h1>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-white text-[11px] font-semibold overflow-hidden">
              {ownerProfile?.avatar ? (
                <img src={ownerProfile.avatar} alt="" className="w-full h-full object-cover" />
              ) : (
                getAvatarFallback(ownerProfile?.nickname)
              )}
            </div>
            <span className="text-[13px] text-slate-500">由 {ownerProfile?.nickname ?? '...'} 建立</span>
          </div>
        </div>

        <div className="h-px bg-slate-100 w-full" />

        <div className="flex items-center justify-center gap-6 w-full flex-wrap">
          <MetaItem icon={CalendarDays} text={`${preview.dayCount} 天`} />
          <MetaItem icon={Users} text={`${preview.memberCount} 位成員`} />
          <MetaItem icon={MapPin} text={`${preview.placeCount} 個景點`} />
        </div>

        <button
          onClick={handleJoin}
          className="w-full h-12 rounded-3xl bg-primary text-white text-sm font-semibold"
        >
          加入這趟旅程
        </button>
        <p className="text-[13px] text-slate-400">不想加入？關閉此頁面即可</p>
      </JoinTripLayout>
    );
  }

  if (screen === 'success' && preview) {
    return (
      <JoinTripLayout>
        <IconCircle bg="#ecfdf5">
          <CircleCheck size={36} className="text-emerald-500" />
        </IconCircle>
        <h1 className="text-slate-900 text-xl font-bold">成功加入！</h1>
        <p className="text-sm text-slate-500 text-center max-w-[340px]">
          你已成功加入「{preview.tripName}」，現在可以開始與夥伴一起規劃行程了。
        </p>
        {members.length > 0 && <AvatarStack profiles={memberProfiles} />}

        <div className="h-px bg-slate-100 w-full" />

        <button
          onClick={() => navigate(`/trips/${preview.tripId}`)}
          className="w-full h-12 rounded-3xl bg-primary text-white text-sm font-semibold"
        >
          前往旅程
        </button>
        <button onClick={() => navigate('/trips')} className="text-[13px] text-slate-400">
          或返回首頁
        </button>
      </JoinTripLayout>
    );
  }

  if (screen === 'expired') {
    return (
      <JoinTripLayout>
        <Clock4 size={48} className="text-amber-500" />
        <h1 className="text-slate-900 text-xl font-semibold">邀請連結已過期</h1>
        <p className="text-sm text-slate-500 text-center max-w-[340px] leading-relaxed">
          此連結已超過有效期限，請聯絡旅程擁有者重新產生連結。
        </p>
        <button className="rounded-3xl bg-slate-100 text-slate-900 text-sm font-medium px-6 py-3">
          聯絡擁有者
        </button>
        <div className="h-px bg-slate-200 w-[340px] max-w-full" />
        <p className="text-xs text-slate-400">錯誤代碼：{errorCode}</p>
      </JoinTripLayout>
    );
  }

  // invalid / error 都走同一個畫面
  return (
    <JoinTripLayout>
      <CircleX size={48} className="text-red-500" />
      <h1 className="text-slate-900 text-xl font-semibold">邀請連結無效</h1>
      <p className="text-sm text-slate-500 text-center max-w-[340px]">
        此連結不存在或已被刪除，請確認連結是否正確。
      </p>
      <div className="h-px bg-slate-200 w-[340px] max-w-full" />
      {errorCode && <p className="text-xs text-slate-400">錯誤代碼：{errorCode}</p>}
    </JoinTripLayout>
  );
}

function JoinTripLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-background-light min-h-screen flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-[420px] rounded-2xl bg-white shadow-lg p-10 flex flex-col items-center gap-5 text-center">
        <div className="flex items-center gap-2 text-primary font-bold text-xl mb-1">
          <Compass size={22} />
          Tripway
        </div>
        {children}
      </div>
    </div>
  );
}

function IconCircle({ bg, children }: { bg: string; children: React.ReactNode }) {
  return (
    <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: bg }}>
      {children}
    </div>
  );
}

function MetaItem({ icon: Icon, text }: { icon: typeof CalendarDays; text: string }) {
  return (
    <div className="flex items-center gap-1.5 text-slate-500 text-[13px]">
      <Icon size={14} className="text-slate-400" />
      {text}
    </div>
  );
}

function Spinner() {
  return <div className="w-10 h-10 rounded-full border-b-2 border-primary animate-spin" />;
}
